import { useQueryClient } from '@tanstack/react-query';
const STORAGE_KEY = 'varos_messages';
const MAX_MESSAGES = 150;
// Helper functions for sessionStorage operations
export function getMessagesFromStorage() {
    try {
        const stored = window.sessionStorage.getItem(STORAGE_KEY);
        if (!stored)
            return [];
        const parsed = JSON.parse(stored);
        // Parse and validate messages, converting date strings back to Date objects
        const validatedMessages = [];
        parsed.forEach((message) => {
            try {
                validatedMessages.push({
                    id: message.id,
                    thread: message.thread,
                    role: message.role,
                    parts: message.parts,
                    context: message.context,
                    createdAt: new Date(message.createdAt),
                });
            }
            catch (error) {
                console.warn(`Failed to parse message ${message.id}:`, error);
            }
        });
        return validatedMessages;
    }
    catch (error) {
        console.warn('Failed to parse stored messages:', error);
        return [];
    }
}
export function saveMessagesToStorage(messages) {
    try {
        // Enforce maximum number of messages by keeping only the most recent ones
        let limitedMessages = messages;
        if (messages.length > MAX_MESSAGES) {
            // Sort by createdAt descending (newest first) and take the first MAX_MESSAGES
            limitedMessages = messages
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .slice(0, MAX_MESSAGES);
        }
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(limitedMessages));
    }
    catch (error) {
        console.error('Error saving messages to sessionStorage:', error);
    }
}
export function generateId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
}
// Shared query key functions
export function getMessageFindQueryKey(props) {
    return ['platform', 'message', 'find', props];
}
export function getMessageLookupQueryKey(props) {
    return ['platform', 'message', 'lookup', props.id];
}
export function getMessageFindPrefix() {
    return ['platform', 'message', 'find'];
}
export function createLocalStorageMessageRepository(config) {
    const { repository } = config;
    return {
        useFind(context, props, options) {
            const query = repository.useQuery({
                ...options,
                queryKey: getMessageFindQueryKey(props),
                async queryFn() {
                    const allMessages = getMessagesFromStorage();
                    // Filter messages by thread if specified
                    let filteredMessages = allMessages;
                    if (props.thread) {
                        filteredMessages = filteredMessages.filter((m) => m.thread === props.thread);
                    }
                    // Apply limit if specified
                    if (props.limit) {
                        // Sort by createdAt ascending (oldest first) for proper conversation order
                        filteredMessages = filteredMessages
                            .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
                            .slice(-props.limit); // Take the most recent messages up to the limit
                    }
                    else {
                        // Sort by createdAt ascending for proper conversation order
                        filteredMessages = filteredMessages.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
                    }
                    return filteredMessages;
                },
            });
            return query;
        },
        useInvalidate(context) {
            const queryClient = useQueryClient();
            return (props) => {
                queryClient.invalidateQueries({
                    queryKey: getMessageFindQueryKey(props),
                });
            };
        },
        // useLookup(context, props, options) {
        //     const query = repository.useQuery({
        //         ...options,
        //         queryKey: getMessageLookupQueryKey(props),
        //         async queryFn() {
        //             const allMessages = getMessagesFromStorage();
        //             const message = allMessages.find((m) => m.id === props.id);
        //             if (!message) {
        //                 throw new Error(`Message with id ${props.id} not found`);
        //             }
        //             return message;
        //         },
        //     });
        //     return query;
        // },
        useCreate(context) {
            const queryClient = useQueryClient();
            return repository.useMutation(context, {
                async mutationFn(props) {
                    const allMessages = getMessagesFromStorage();
                    const messageId = props.id || generateId();
                    // Check if message with this ID already exists
                    const existingMessageIndex = allMessages.findIndex((m) => m.id === messageId);
                    if (existingMessageIndex !== -1) {
                        // Update the existing message with new parts (for streaming messages)
                        const existingMessage = allMessages[existingMessageIndex];
                        const updatedMessage = {
                            ...existingMessage,
                            parts: props.parts,
                            context: props.context || existingMessage.context,
                        };
                        const updatedMessages = [...allMessages];
                        updatedMessages[existingMessageIndex] = updatedMessage;
                        saveMessagesToStorage(updatedMessages);
                        return updatedMessage;
                    }
                    const newMessage = {
                        id: messageId,
                        thread: props.thread,
                        role: props.role || 'user',
                        parts: props.parts,
                        context: props.context || [],
                        createdAt: new Date(),
                    };
                    const updatedMessages = [...allMessages, newMessage];
                    saveMessagesToStorage(updatedMessages);
                    return newMessage;
                },
                onMutate: async (props) => {
                    // Cancel any outgoing refetches to prevent race conditions
                    await queryClient.cancelQueries({
                        queryKey: getMessageFindPrefix(),
                    });
                    const messageId = props.id || generateId();
                    // Find all relevant queries that need updating
                    const allThreadQueryKey = getMessageFindQueryKey({});
                    const threadSpecificQueryKey = getMessageFindQueryKey({
                        thread: props.thread,
                    });
                    // Snapshot previous values for rollback
                    const previousAllData = queryClient.getQueryData(allThreadQueryKey);
                    const previousThreadData = queryClient.getQueryData(threadSpecificQueryKey);
                    // Check if message already exists in any cache
                    const existingInAll = Array.isArray(previousAllData)
                        ? previousAllData.find((m) => m.id === messageId)
                        : null;
                    const existingInThread = Array.isArray(previousThreadData)
                        ? previousThreadData.find((m) => m.id === messageId)
                        : null;
                    if (existingInAll || existingInThread) {
                        // Create the updated message
                        const updatedMessage = {
                            ...(existingInAll || existingInThread),
                            parts: props.parts,
                            context: props.context ||
                                (existingInAll || existingInThread).context,
                        };
                        // Update all relevant queries
                        if (previousAllData) {
                            queryClient.setQueryData(allThreadQueryKey, (old) => {
                                const currentMessages = old || [];
                                return currentMessages
                                    .map((m) => m.id === messageId ? updatedMessage : m)
                                    .sort((a, b) => a.createdAt.getTime() -
                                    b.createdAt.getTime());
                            });
                        }
                        if (previousThreadData) {
                            queryClient.setQueryData(threadSpecificQueryKey, (old) => {
                                const currentMessages = old || [];
                                return currentMessages
                                    .map((m) => m.id === messageId ? updatedMessage : m)
                                    .sort((a, b) => a.createdAt.getTime() -
                                    b.createdAt.getTime());
                            });
                        }
                        return {
                            previousAllData,
                            previousThreadData,
                            newMessage: updatedMessage,
                            allThreadQueryKey,
                            threadSpecificQueryKey,
                            isUpdate: true,
                        };
                    }
                    // Create new message optimistically
                    const newMessage = {
                        id: messageId,
                        thread: props.thread,
                        role: props.role || 'user',
                        parts: props.parts,
                        context: props.context || [],
                        createdAt: new Date(),
                    };
                    // Update all relevant caches
                    if (previousAllData !== undefined) {
                        queryClient.setQueryData(allThreadQueryKey, (old) => {
                            const currentMessages = old || [];
                            return [...currentMessages, newMessage].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
                        });
                    }
                    queryClient.setQueryData(threadSpecificQueryKey, (old) => {
                        const currentMessages = old || [];
                        return [...currentMessages, newMessage].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
                    });
                    return {
                        previousAllData,
                        previousThreadData,
                        newMessage,
                        allThreadQueryKey,
                        threadSpecificQueryKey,
                        isUpdate: false,
                    };
                },
                onError: (err, variables, context) => {
                    console.error('Message creation failed:', err);
                    // Rollback all optimistic updates
                    if (context?.previousAllData !== undefined &&
                        context?.allThreadQueryKey) {
                        queryClient.setQueryData(context.allThreadQueryKey, context.previousAllData);
                    }
                    if (context?.previousThreadData !== undefined &&
                        context?.threadSpecificQueryKey) {
                        queryClient.setQueryData(context.threadSpecificQueryKey, context.previousThreadData);
                    }
                },
                async onSuccess(data, variables) {
                    // Aggressively invalidate and refetch all message queries
                    await Promise.all([
                        // Invalidate all message queries
                        queryClient.invalidateQueries({
                            queryKey: getMessageFindPrefix(),
                        }),
                        // Invalidate thread-specific queries
                        queryClient.invalidateQueries({
                            queryKey: getMessageFindQueryKey({
                                thread: variables.thread,
                            }),
                        }),
                    ]);
                    // Force immediate refetch of the specific thread's messages
                    await queryClient.refetchQueries({
                        queryKey: getMessageFindQueryKey({ thread: variables.thread }),
                    });
                },
            });
        },
        useUpdate(context) {
            const queryClient = useQueryClient();
            return repository.useMutation(context, {
                async mutationFn(props) {
                    const allMessages = getMessagesFromStorage();
                    const messageIndex = allMessages.findIndex((m) => m.id === props.id);
                    if (messageIndex === -1) {
                        // Message doesn't exist yet - this might happen due to race conditions
                        // Instead of throwing an error, we'll create it as a new message
                        // We need the thread info to create a new message
                        // For now, let's throw an error with more context
                        throw new Error(`Message with id ${props.id} not found for update. Available message IDs: ${allMessages.map((m) => m.id).join(', ')}`);
                    }
                    if (messageIndex === -1) {
                        // Message doesn't exist yet - this might happen due to race conditions
                        // Instead of throwing an error, we'll create it as a new message
                        console.warn(`Message with id ${props.id} not found, creating as new message`);
                        // We need the thread info to create a new message
                        // For now, let's throw an error with more context
                        throw new Error(`Message with id ${props.id} not found for update. Available message IDs: ${allMessages.map((m) => m.id).join(', ')}`);
                    }
                    const existingMessage = allMessages[messageIndex];
                    const updatedMessage = {
                        ...existingMessage,
                        parts: props.parts ?? existingMessage.parts,
                        context: props.context ?? existingMessage.context,
                    };
                    const updatedMessages = [...allMessages];
                    updatedMessages[messageIndex] = updatedMessage;
                    saveMessagesToStorage(updatedMessages);
                    console.log(`Successfully updated message ${props.id}`);
                    return updatedMessage;
                },
                onMutate: async (props) => {
                    // Cancel any outgoing refetches
                    await queryClient.cancelQueries({
                        queryKey: ['platform', 'message'],
                    });
                    // Get the current message from storage to determine thread
                    const allMessages = getMessagesFromStorage();
                    const existingMessage = allMessages.find((m) => m.id === props.id);
                    if (!existingMessage) {
                        console.warn(`Message with id ${props.id} not found in storage for optimistic update`);
                        // Don't throw error in onMutate - let the mutationFn handle it
                        return {
                            previousLookupData: undefined,
                            previousFindData: undefined,
                            lookupQueryKey: undefined,
                            findQueryKey: undefined,
                        };
                    }
                    // Snapshot previous values for rollback
                    const lookupQueryKey = getMessageLookupQueryKey({ id: props.id });
                    const findQueryKey = getMessageFindQueryKey({
                        thread: existingMessage.thread,
                    });
                    const previousLookupData = queryClient.getQueryData(lookupQueryKey);
                    const previousFindData = queryClient.getQueryData(findQueryKey);
                    // Create the optimistically updated message
                    const updatedMessage = {
                        ...existingMessage,
                        parts: props.parts ?? existingMessage.parts,
                        context: props.context ?? existingMessage.context,
                    };
                    // Optimistically update the lookup query cache
                    queryClient.setQueryData(lookupQueryKey, updatedMessage);
                    // Optimistically update the find query cache
                    queryClient.setQueryData(findQueryKey, (old) => {
                        const currentMessages = old || [];
                        return currentMessages.map((m) => m.id === props.id ? updatedMessage : m);
                    });
                    // Return context for rollback
                    return {
                        previousLookupData,
                        previousFindData,
                        lookupQueryKey,
                        findQueryKey,
                    };
                },
                onError: (err, variables, context) => {
                    console.error(`Failed to update message ${variables.id}:`, err);
                    // Rollback the optimistic updates
                    if (context?.lookupQueryKey &&
                        context?.previousLookupData !== undefined) {
                        queryClient.setQueryData(context.lookupQueryKey, context.previousLookupData);
                    }
                    if (context?.findQueryKey &&
                        context?.previousFindData !== undefined) {
                        queryClient.setQueryData(context.findQueryKey, context.previousFindData);
                    }
                },
                async onSuccess(data) {
                    // Invalidate related queries to ensure consistency
                    await queryClient.refetchQueries({
                        queryKey: ['platform', 'message'],
                    });
                },
            });
        },
    };
}
//# sourceMappingURL=localStorageMessageRepository.js.map